/**
 * A navigation to a different screen starts at the TOP of that screen.
 *
 * The router does not do this by itself. `createBrowserRouter` resets scroll only when a
 * `<ScrollRestoration />` is rendered, and none ever was — so the window kept whatever offset the
 * previous screen had left it at. Clicking a module in the sidebar from halfway down the dashboard
 * opened the next screen halfway down too, and on a screen shorter than that offset what came into
 * view was the empty space beneath its content: blank, and black in the dark theme. Reported
 * 2026-10-10: a module "should take to first page first not the last which is blank".
 *
 * Why this and not `<ScrollRestoration />`:
 *
 *   - It keys on the PATHNAME. `ScrollRestoration` resets on every non-POP navigation, search
 *     params included, so a tab or page size written to the query string would throw the reader
 *     back to the top of the screen they are still on. A query change is the same screen.
 *   - A `replace` to the same path is not a new screen either. The composer and the documents
 *     screen issue one on arrival to clear the files handed to them in router state.
 *   - It runs as a LAYOUT effect, so it lands before any destination's ordinary `useEffect` —
 *     a screen that scrolls something into view on arrival still gets the last word.
 *
 * The first render is left alone: there is no previous screen to have inherited an offset from,
 * and a reload is the browser's to restore.
 */
import { useLayoutEffect, useRef } from 'react'
import { useLocation } from 'react-router-dom'

export function useScrollResetOnNavigate(): void {
  const { pathname } = useLocation()
  const previous = useRef(pathname)
  useLayoutEffect(() => {
    if (previous.current === pathname) return
    previous.current = pathname
    window.scrollTo(0, 0)
  }, [pathname])
}
